import './siteHeader.scss';

import React, { useEffect, useState } from 'react';

import {
    Icon,
    LocalizedLink,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import {
    QuickCart,
    QuickCartIcon,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/QuickCart';
import { graphql, useStaticQuery } from 'gatsby';
import {
    useCustomerAccount,
    useSignOutAction,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/customerAccount';

import { CSSTransition } from 'react-transition-group';
import SearchBar from '../../components/search/SearchBar';
import Sidenav from './Sidenav';
import SiteLocaleSelectionPopupMenu from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/SiteLocale/SiteLocaleSelectionPopupMenu';
import closeHamburger from '../../images/icons/close-burger-menu.svg';
import { uniqBy } from 'lodash';
import { useBoutikService } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/boutikService';
import { useCartContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';
import { LoginDrawer } from '@sbi-international/boutik.gatsby.plugin.account';
import ventisLogo from '../../images/Logo-Ventis.svg';

export default function SiteHeader(props: {
    categoryTypes: any;
    allCategories: any;
}) {
    const { categoryTypes, allCategories } = props;
    const [isSidenavOpen, setSidenavOpen] = useState(false);
    const { t } = useTranslation('layout');
    const { currentLocale, navigateTranslated } = usePageLocalization();

    const [isQuickLoginVisible, setIsQuickLoginVisible] =
        useState<boolean>(false);

    function Logo() {
        return (
            <LocalizedLink to='/'>
                <img alt='Ventis-Logo.png' src={ventisLogo} />
            </LocalizedLink>
        );
    }

    const data = useStaticQuery(
        graphql`
            query {
                alerte: allProductsJson(
                    filter: { code: { eq: "occ_banniere-alerte" } }
                ) {
                    nodes {
                        code
                        title {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        `
    );

    const isUSA = currentLocale === 'en_US';

    function HeaderNav() {
        return (
            <ul className='header-nav'>
                <li>
                    <div className='nav-title'>
                        {t('navigation.appliances.appliances')}
                    </div>
                    <div className='nav-dropdown'>
                        <LocalizedLink to='/wood-fireplaces/'>
                            {t('navigation.appliances.fireplaces')}
                        </LocalizedLink>
                        <LocalizedLink to='/stoves/'>
                            {t('navigation.appliances.stoves')}
                        </LocalizedLink>
                        <LocalizedLink to='/wood-inserts/'>
                            {t('navigation.appliances.inserts')}
                        </LocalizedLink>
                    </div>
                </li>
                {!isUSA && (
                    <li>
                        <div className='nav-title'>
                            {t('navigation.ventings-chimney.ventings-chimney')}
                        </div>
                        <div className='nav-dropdown'>
                            {categoryTypes.map(
                                (categoryType: any, index: number) => {
                                    const categoryNames = allCategories.filter(
                                        (category: any) =>
                                            category.id.includes(
                                                categoryTypes[index]?.type
                                            )
                                    );
                                    const uniqueCategories: any = uniqBy(
                                        categoryNames,
                                        'name'
                                    ).sort((a: any, b: any) =>
                                        a.name > b.name ? 1 : -1
                                    );
                                    return (
                                        <LocalizedLink
                                            key={index}
                                            to={`/venting/?filter=${categoryType.url}&subfilter=${uniqueCategories?.[0]?.url_name}&diameter=none`}
                                        >
                                            {categoryType.type}
                                        </LocalizedLink>
                                    );
                                }
                            )}
                        </div>
                    </li>
                )}
                <li>
                    <div className='nav-title'>
                        {t('navigation.parts-accessories.parts-accessories')}
                    </div>
                    <div className='nav-dropdown'>
                        <LocalizedLink to='/archives/'>
                            {t('navigation.parts-accessories.replacement-part')}
                        </LocalizedLink>
                        <LocalizedLink to='/accessories/'>
                            {t('navigation.parts-accessories.accessories')}
                        </LocalizedLink>
                    </div>
                </li>
                <li>
                    <div className='nav-title'>
                        {t('navigation.service-support.service-support')}
                    </div>
                    <div className='nav-dropdown'>
                        <LocalizedLink to='/contact/'>
                            {t('navigation.service-support.contact')}
                        </LocalizedLink>
                        <LocalizedLink to='/articles/'>
                            {t('navigation.service-support.faq')}
                        </LocalizedLink>
                        <LocalizedLink to='/warranty/'>
                            {t('navigation.service-support.warranty')}
                        </LocalizedLink>
                        <LocalizedLink to='/gov-incentives/'>
                            {t('navigation.service-support.gov-incentives')}
                        </LocalizedLink>
                        <LocalizedLink to='/archives/'>
                            {t('navigation.service-support.manuals')}
                        </LocalizedLink>
                        <LocalizedLink to='/comparator/'>
                            {t('navigation.service-support.comparator')}
                        </LocalizedLink>
                    </div>
                </li>
            </ul>
        );
    }

    function LoginButton() {
        const { t } = useTranslation('layout');

        return (
            <span
                onClick={() => setIsQuickLoginVisible(true)}
                className='accountText'
            >
                {t('account.myaccount')}
            </span>
        );
    }

    function AccountButton(props: { currentUser: any }) {
        const { t } = useTranslation('layout');
        const { currentUser } = props;
        const [signOut] = useSignOutAction();
        const { setQuickCartVisible } = useCartContext();

        const svgAccount =
            'M13.979,10.587a8.157,8.157,0,0,0-3.112-1.952,4.734,4.734,0,1,0-5.357,0A8.2,8.2,0,0,0,0,16.377H1.279a6.909,6.909,0,1,1,13.818,0h1.279A8.135,8.135,0,0,0,13.979,10.587Zm-5.79-2.4a3.455,3.455,0,1,1,3.455-3.455A3.458,3.458,0,0,1,8.188,8.188Z';

        const fullname = currentUser
            ? `${currentUser.firstname} ${currentUser.lastname}`
            : '';

        return (
            <>
                <div>
                    <LocalizedLink to='/account/my-account/'>
                        <Icon d={svgAccount} size='20' />
                        <span className='accountText'>{fullname}</span>
                    </LocalizedLink>
                </div>
                <div
                    className={
                        fullname.length > 20
                            ? 'accountPopup bigname'
                            : 'accountPopup'
                    }
                >
                    <ul className='accountMenuList'>
                        <li>
                            <LocalizedLink to='/account/my-account/'>
                                {t('account.myaccount')}
                            </LocalizedLink>
                        </li>
                        <li>
                            <LocalizedLink to='/account/my-orders/'>
                                {t('account.myorders')}
                            </LocalizedLink>
                        </li>
                        <li>
                            <span onClick={() => setQuickCartVisible(true)}>
                                {t('account.mycart')}
                            </span>
                        </li>

                        <li className='logOut' onClick={signOut}>
                            <span>{t('account.logout')}</span>
                        </li>
                    </ul>
                </div>
            </>
        );
    }

    function HeaderShortcuts() {
        const positionIcon =
            'M6.056,17.637C.948,10.232,0,9.472,0,6.75a6.75,6.75,0,0,1,13.5,0c0,2.722-.948,3.482-6.056,10.887a.844.844,0,0,1-1.387,0ZM6.75,9.562A2.812,2.812,0,1,0,3.937,6.75,2.812,2.812,0,0,0,6.75,9.562Z';
        const searchIcon =
            'M17.852,16.283H17l-.321-.268a7.09,7.09,0,0,0,1.656-4.553,6.917,6.917,0,1,0-6.89,6.963,7.158,7.158,0,0,0,4.54-1.66l.32.268v.857l5.341,5.356,1.6-1.607Zm-6.409,0a4.82,4.82,0,1,1,4.807-4.82A4.794,4.794,0,0,1,11.443,16.283Z';
        const cartIcon =
            'M9.384,19.186a1.7,1.7,0,1,1-1.7-1.7,1.7,1.7,0,0,1,1.7,1.7Zm11.355,0a1.7,1.7,0,1,1-1.7-1.7A1.7,1.7,0,0,1,20.739,19.186Zm0-7.381V4.992H7.113A1.136,1.136,0,0,0,5.977,3.856H2.571V4.992H4.842L5.695,12.3a2.271,2.271,0,0,0,1.418,4.044H20.739V15.211H7.113a1.136,1.136,0,0,1-1.136-1.136s0-.008,0-.012L20.739,11.8Z';

        const { isAuthenticated } = useBoutikService();
        const { currentUser } = useCustomerAccount();

        const LoginOrAccountBtn = !isAuthenticated ? (
            <LoginButton />
        ) : (
            <AccountButton currentUser={currentUser} />
        );
        const { currentLocale } = usePageLocalization();
        const displayRetailer = currentLocale !== 'en_US';

        return (
            <div className='header-shortcuts'>
                <span
                    className='hamburger-menu'
                    onClick={() => {
                        setSidenavOpen(!isSidenavOpen);
                    }}
                >
                    {isSidenavOpen ? (
                        <img src={closeHamburger} alt='' width='18' />
                    ) : (
                        <Icon
                            d='M 6 9 A 2.0002 2.0002 0 1 0 6 13 L 42 13 A 2.0002 2.0002 0 1 0 42 9 L 6 9 z M 6 22 A 2.0002 2.0002 0 1 0 6 26 L 42 26 A 2.0002 2.0002 0 1 0 42 22 L 6 22 z M 6 35 A 2.0002 2.0002 0 1 0 6 39 L 42 39 A 2.0002 2.0002 0 1 0 42 35 L 6 35 z'
                            size='24'
                            viewbox='0 0 48 48'
                            color='white'
                        />
                    )}
                </span>
                {!isSidenavOpen && (
                    <>
                        <div className='shortcut-link lang'>
                            <SiteLocaleSelectionPopupMenu showCurrency={true} />
                        </div>

                        <div className='shortcut-link cart-header-icon'>
                            <QuickCartIcon />
                        </div>

                        <div className='shortcut-link account'>
                            {LoginOrAccountBtn}
                        </div>

                        <div className='separator'>|</div>

                        <div
                            className='shortcut-link search-input'
                            style={{ marginRight: '1rem' }}
                        >
                            <SearchBar
                                key='searchBarComponent'
                                searchIcon={searchIcon}
                            />
                        </div>

                        {displayRetailer && (
                            <div className='shortcut-link retailer'>
                                <LocalizedLink
                                    className='contact-link'
                                    to='/find-retailer/'
                                >
                                    <Icon
                                        d={positionIcon}
                                        size='18'
                                        viewbox='0 0 13.5 18'
                                        transform='translate(0 0)'
                                    />
                                    <span className='shortcut-text'>
                                        {t('navigation.find-retailer')}
                                    </span>
                                </LocalizedLink>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    function AlertBanner(props: { bannerText: string }) {
        if (props.bannerText)
            return (
                <div className='alert-banner'>
                    <span className='banner-text'>{props.bannerText}</span>
                </div>
            );
        else return null;
    }

    useEffect(() => {
        if (isSidenavOpen) {
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.documentElement.style.overflow = 'scroll';
        }
    }, [isSidenavOpen]);

    return (
        <>
            <header>
                <CSSTransition
                    in={isSidenavOpen}
                    timeout={150}
                    classNames='pageOverlay'
                    unmountOnExit
                >
                    <div
                        className='pageOverlay'
                        onClick={() => setSidenavOpen(false)}
                    ></div>
                </CSSTransition>

                <LoginDrawer
                    isOpen={isQuickLoginVisible}
                    onClose={() => setIsQuickLoginVisible(false)}
                    onCreateAccountClick={
                        () =>
                            setTimeout(
                                () => navigateTranslated('/register/'),
                                300
                            ) // setTimeout pour l'animation
                    }
                    onForgotPasswordClick={
                        () =>
                            setTimeout(
                                () => navigateTranslated('/forgot-password/'),
                                300
                            ) // setTimeout pour l'animation
                    }
                />

                <div className='header'>
                    <div className='header-logo'>
                        <Logo />
                        <div className='logo-bg'></div>
                    </div>
                    <div className='header-content'>
                        <HeaderShortcuts />
                        <HeaderNav />
                    </div>
                </div>

                <CSSTransition
                    in={isSidenavOpen}
                    timeout={150}
                    classNames='sidenav-menu'
                    unmountOnExit
                >
                    <div className='sidenav-menu'>
                        <Sidenav
                            setSidenavOpen={setSidenavOpen}
                            categoryTypes={categoryTypes}
                            allCategories={allCategories}
                        />
                    </div>
                </CSSTransition>
            </header>
            <AlertBanner
                bannerText={data?.alerte?.nodes[0]?.title?.[currentLocale]}
            />
            <QuickCart />
        </>
    );
}
